import { QuestionTypes } from '@showbie-socrative/socrative-utils';

import { QuestionTypes as AnalyticsQuestionTypes } from '~analytics/types';
import { countMathEquations } from '~analytics/utils';
import { QuizAnswer, QuizQuestion } from '~teacher/models/quizzes';

import { Answer, Question } from '../../api/responses';
import { sendQuestionSavedEvent } from '../index';
import { EventAdapter } from './EventAdapter';

interface QuestionSavedAdapterParams {
  modifiedQuestion: Question;
  modifiedAnswers: Array<Answer>;
  originalQuestion: QuizQuestion | null;
  originalAnswers: Array<QuizAnswer> | null;
  generationSource?: string;
}

export class QuestionSavedEventAdapter extends EventAdapter {
  protected intercomEventKey = null;
  protected amplitudeEventFunction = sendQuestionSavedEvent;

  constructor({
    originalQuestion,
    modifiedQuestion,
    originalAnswers,
    modifiedAnswers,
    generationSource = null,
  }: QuestionSavedAdapterParams) {
    super();

    try {
      const originalQuestionEquations = countMathEquations({
        question: originalQuestion,
        answers: originalAnswers,
      });
      const modifiedQuestionEquations = countMathEquations({
        question: modifiedQuestion,
        answers: modifiedAnswers,
      });

      this.eventData = {
        is_new_question: originalQuestion == null,
        question_type: this.mapQuestionType(modifiedQuestion.type),
        original_question_weight: originalQuestion?.gradingWeight,
        saved_question_weight: modifiedQuestion.gradingWeight,
        original_has_explanation_image: !!originalQuestion?.explanationImage,
        saved_has_explanation_image: !!modifiedQuestion.explanationImage,
        original_has_question_image: !!originalQuestion?.questionImage,
        saved_has_question_image: !!modifiedQuestion.questionImage,
        original_answer_count: (originalAnswers ?? []).length,
        saved_answer_count: (modifiedAnswers ?? []).length,
        saved_formula_count: modifiedQuestionEquations.formulaCount,
        saved_chemical_count: modifiedQuestionEquations.chemicalCount,
        original_formula_count: originalQuestionEquations.formulaCount,
        original_chemical_count: originalQuestionEquations.chemicalCount,
        original_has_question_video: !!originalQuestion?.questionVideo,
        saved_has_question_video: !!modifiedQuestion.questionVideo,
        generation_source: generationSource,
      };
    } catch (err) {
      console.error('Error parsing analytics data', err);
    }
  }

  mapQuestionType(questionType: QuestionTypes | string) {
    switch (questionType) {
      case QuestionTypes.TrueFalse:
        return AnalyticsQuestionTypes.TrueFalse;
      case QuestionTypes.MultipleChoice:
        return AnalyticsQuestionTypes.MultipleChoice;
      case QuestionTypes.FreeResponse:
        return AnalyticsQuestionTypes.ShortAnswer;
      default:
        return null;
    }
  }
}
